/** Version 1 pure birth calculations; missing observations never become normal. */
export interface BirthEvent {
  id: string;
  root_id?: string;
  episode_id: string;
  subject_id: string;
  sequence: number;
  status: string;
  event_type: string;
  occurred_at: string;
  payload: Record<string, unknown>;
}
export interface BirthResult<T> {
  version: 1;
  value: T;
  inputs_used: unknown;
  warnings: string[];
}
export const number = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
export function instant(value: unknown): number | null {
  if (typeof value !== 'string' || !/(Z|[+-]\d\d:\d\d)$/.test(value)) return null;
  const day = value.slice(0, 10);
  const calendar = new Date(day + 'T00:00:00Z');
  if (
    !/^\d{4}-\d{2}-\d{2}T/.test(value) ||
    !Number.isFinite(calendar.getTime()) ||
    calendar.toISOString().slice(0, 10) !== day
  )
    return null;
  const n = Date.parse(value);
  return Number.isFinite(n) ? n : null;
}
export function result<T>(value: T, inputs_used: unknown, warnings: string[] = []): BirthResult<T> {
  return { version: 1, value, inputs_used, warnings };
}
export function activeBirthEvents(events: BirthEvent[], episodeId: string, now: string): BirthEvent[] {
  const heads = new Map<string, BirthEvent>();
  for (const e of events) {
    if (e.episode_id !== episodeId) continue;
    const key = e.root_id ?? e.id;
    if (!heads.has(key) || e.sequence > heads.get(key)!.sequence) heads.set(key, e);
  }
  const end = instant(now);
  return [...heads.values()]
    .filter(
      (e) => e.status === 'ACTIVE' && instant(e.occurred_at) !== null && end !== null && instant(e.occurred_at)! <= end,
    )
    .sort((a, b) => instant(a.occurred_at)! - instant(b.occurred_at)! || a.sequence - b.sequence);
}
export function minutesAfterBirth(at: unknown, birthAt: unknown) {
  const a = instant(at),
    b = instant(birthAt),
    valid = a !== null && b !== null && a >= b;
  return result(
    valid ? (a - b) / 60000 : null,
    { at, birth_at: birthAt },
    valid ? [] : ['Missing, invalid or reversed timestamps.'],
  );
}
export function newbornAgeHours(at: unknown, birthAt: unknown) {
  const r = minutesAfterBirth(at, birthAt);
  return result(r.value !== null ? Math.round((r.value / 60) * 10) / 10 : null, r.inputs_used, r.warnings);
}
export function apgarTotal(components: Record<string, unknown>) {
  const vals = ['heart_rate', 'resp_effort', 'reflex', 'tone', 'color'].map((k) => components[k]);
  const valid = vals.every((v) => number(v) && Number.isInteger(v) && v >= 0 && v <= 2);
  return result(
    valid ? (vals as number[]).reduce((a, b) => a + b, 0) : null,
    components,
    valid ? [] : ['All five integer components (0–2) required.'],
  );
}
export function shockIndex(hr: unknown, sbp: unknown) {
  const valid = number(hr) && number(sbp) && hr > 0 && sbp > 0;
  return result(
    valid ? hr / sbp : null,
    { hr, sbp },
    valid ? [] : ['Positive heart rate and systolic pressure required.'],
  );
}
export function weightChangePct(weight: unknown, birthWeight: unknown) {
  const valid = number(weight) && number(birthWeight) && weight > 0 && birthWeight > 0;
  return result(
    valid ? ((weight - birthWeight) / birthWeight) * 100 : null,
    { weight, birth_weight: birthWeight },
    valid ? [] : ['Positive weights in the same unit required.'],
  );
}
export function bishopScore(p: Record<string, unknown>) {
  const d = p.dilation_cm,
    e = p.effacement_pct,
    s = p.station;
  const ds =
    d === 0
      ? 0
      : number(d) && d >= 1 && d <= 2
        ? 1
        : number(d) && d >= 3 && d <= 4
          ? 2
          : number(d) && d >= 5 && d <= 10
            ? 3
            : null;
  const es =
    number(e) && e >= 0 && e <= 30
      ? 0
      : number(e) && e >= 40 && e <= 50
        ? 1
        : number(e) && e >= 60 && e <= 70
          ? 2
          : number(e) && e >= 80 && e <= 100
            ? 3
            : null;
  const ss =
    number(s) && Number.isInteger(s)
      ? (({ '-3': 0, '-2': 1, '-1': 2, '0': 2, '1': 3, '2': 3 } as Record<string, number>)[s] ?? null)
      : null;
  const cs =
    ({ firm: 0, medium: 1, soft: 2 } as Record<string, number>)[String(p.cervix_consistency ?? '').toLowerCase()] ??
    null;
  const ps =
    ({ posterior: 0, midposition: 1, anterior: 2 } as Record<string, number>)[
      String(p.cervix_position ?? '').toLowerCase()
    ] ?? null;
  const values = [ds, es, ss, cs, ps];
  return result(
    values.every((v) => v !== null) ? (values as number[]).reduce((a, b) => a + b, 0) : null,
    p,
    values.every((v) => v !== null)
      ? []
      : ['Complete original Bishop categories required; no rounding or conversion from cervical length.'],
  );
}
export function cumulativeBloodLoss(
  events: BirthEvent[],
  episodeId: string,
  motherId: string,
  birthAt: string | null,
  now: string,
) {
  const start = instant(birthAt),
    selected = activeBirthEvents(events, episodeId, now).filter(
      (e) =>
        e.subject_id === motherId &&
        e.event_type === 'PP.BLOOD_LOSS' &&
        start !== null &&
        instant(e.occurred_at)! >= start,
    );
  const amounts = selected.filter((e) => number(e.payload.amount_ml) && e.payload.amount_ml >= 0);
  const totals: { total_ml: number | null; ebl_ml: number | null; qbl_ml: number | null; other_ml: number | null } =
    amounts.length
      ? { total_ml: 0, ebl_ml: 0, qbl_ml: 0, other_ml: 0 }
      : { total_ml: null, ebl_ml: null, qbl_ml: null, other_ml: null };
  for (const e of amounts) {
    const n = e.payload.amount_ml as number;
    totals.total_ml! += n;
    const method = String(e.payload.method ?? '').toUpperCase();
    totals[
      ['EBL', 'ESTIMATED'].includes(method) ? 'ebl_ml' : ['QBL', 'QUANTIFIED'].includes(method) ? 'qbl_ml' : 'other_ml'
    ]! += n;
  }
  const warnings = ['Documented incremental amounts only; do not sum repeated cumulative estimates.'];
  if (amounts.length !== selected.length) warnings.push('Descriptive blood loss without numeric quantity is excluded.');
  return result(
    totals,
    selected.map((e) => e.id),
    warnings,
  );
}
export function stageDurations(
  events: BirthEvent[],
  episodeId: string,
  motherId: string,
  fetusId: string,
  now: string,
  fallbackToLaborStart = false,
) {
  const all = activeBirthEvents(events, episodeId, now),
    maternal = all.filter((e) => e.subject_id === motherId);
  const milestone = (label: string) =>
    maternal.find((e) => e.event_type === 'MILESTONE' && e.payload.milestone === label);
  const onset = milestone('LABOR_START'),
    active = milestone('ACTIVE_LABOR'),
    pushing = milestone('PUSHING_START'),
    full =
      milestone('SECOND_STAGE') ??
      maternal.find((e) => e.event_type === 'LABOR.PROGRESS' && e.payload.dilation_cm === 10),
    birth = all.find((e) => e.subject_id === fetusId && e.event_type === 'BIRTH'),
    placenta = maternal.find(
      (e) => e.event_type === 'PLACENTA' && birth && instant(e.occurred_at)! >= instant(birth.occurred_at)!,
    ),
    rom = maternal.find((e) => e.event_type === 'ROM');
  const pairs: Record<string, [BirthEvent | undefined, BirthEvent | undefined]> = {
    first_stage: [active ?? (fallbackToLaborStart ? onset : undefined), full],
    second_stage: [full, birth],
    pushing: [pushing, birth],
    third_stage: [birth, placenta],
    total_labor: [onset, birth],
    rom_to_birth: [rom, birth],
  };
  return Object.fromEntries(
    Object.entries(pairs).map(([key, [a, b]]) => [
      key,
      {
        ...minutesAfterBirth(b?.occurred_at ?? null, a?.occurred_at ?? null),
        inputs_used: [a, b].filter((e): e is BirthEvent => !!e).map((e) => e.id),
      },
    ]),
  );
}
export function gbsProphylaxisAdequate(
  events: BirthEvent[],
  episodeId: string,
  motherId: string,
  birthAt: string,
  drugs: string[],
  minimumHours: number,
  reviewed = false,
) {
  if (!reviewed || !drugs.length || !number(minimumHours) || minimumHours <= 0 || instant(birthAt) === null)
    return result(null, [], ['Reviewed drug/route-duration criteria and birth time required.']);
  const eligible = activeBirthEvents(events, episodeId, birthAt);
  const labor = eligible.find(
    (e) =>
      e.subject_id === motherId &&
      e.event_type === 'MILESTONE' &&
      ['LABOR_START', 'ADMITTED'].includes(String(e.payload.milestone)),
  );
  if (!labor)
    return result(null, [], ['Documented labor/admission start required to identify intrapartum administrations.']);
  const candidates = eligible.filter(
    (e) =>
      instant(e.occurred_at)! >= instant(labor.occurred_at)! &&
      e.subject_id === motherId &&
      e.event_type === 'MED.ADMIN' &&
      e.payload.status === 'Administered' &&
      String(e.payload.route ?? '').toUpperCase() === 'IV' &&
      drugs.map((d) => d.toLowerCase()).includes(String(e.payload.drug ?? '').toLowerCase()),
  );
  return result(
    candidates.length
      ? candidates.some((e) => minutesAfterBirth(birthAt, e.occurred_at).value! >= minimumHours * 60)
      : null,
    candidates.map((e) => e.id),
    [
      'Duration criterion only; verify indication, dose and complete regimen. Missing documentation is not evidence of no prophylaxis.',
    ],
  );
}
